<template>
  <v-container>
    <v-card>
      <v-card-title>WebSocket Connection Test</v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <h3>Connection Status</h3>
            <p>Status: <span :class="statusColor">{{ connectionStatus }}</span></p>
            <p>Token: {{ hasToken ? 'Available ✅' : 'Missing ❌' }}</p>
            <p>User: {{ user ? user.user_name || user.email : 'Not logged in' }}</p>
          </v-col>
        </v-row>
        
        <v-row>
          <v-col cols="12">
            <v-btn @click="testConnection" :disabled="isConnecting" color="primary">
              {{ isConnecting ? 'Connecting...' : 'Test Connection' }}
            </v-btn>
            <v-btn @click="disconnect" class="ml-2" color="secondary">
              Disconnect
            </v-btn>
            <v-btn @click="clearLogs" class="ml-2" color="warning">
              Clear Logs
            </v-btn>
            <v-btn @click="refreshAuth" class="ml-2" color="info">
              Refresh Auth
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3>Configuration</h3>
            <pre>{{ config }}</pre>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3>Environment Variables</h3>
            <pre>{{ envVars }}</pre>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <h3>Logs</h3>
            <v-card outlined class="pa-2" style="max-height: 300px; overflow-y: auto;">
              <div v-for="(log, index) in logs" :key="index" class="mb-1">
                <small class="grey--text">{{ log.time }}</small> - 
                <span :class="log.type === 'error' ? 'red--text' : log.type === 'success' ? 'green--text' : ''">
                  {{ log.message }}
                </span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import WebSocketService from '@/services/websocket'

export default {
  name: 'WebSocketTest',
  data() {
    return {
      connectionStatus: 'Disconnected',
      isConnecting: false,
      logs: [],
      config: {}
    }
  },
  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    hasToken() {
      return !!this.$store.getters['auth/token']
    },
    statusColor() {
      switch(this.connectionStatus) {
        case 'Connected': return 'green--text'
        case 'Connecting': return 'orange--text'
        case 'Error': return 'red--text'
        default: return 'grey--text'
      }
    },
    envVars() {
      return {
        VUE_APP_API_URL: process.env.VUE_APP_API_URL,
        VUE_APP_WEBSOCKET_HOST: process.env.VUE_APP_WEBSOCKET_HOST,
        VUE_APP_WEBSOCKET_PORT: process.env.VUE_APP_WEBSOCKET_PORT,
        VUE_APP_WEBSOCKET_KEY: process.env.VUE_APP_WEBSOCKET_KEY,
        VUE_APP_WEBSOCKET_SCHEME: process.env.VUE_APP_WEBSOCKET_SCHEME,
        NODE_ENV: process.env.NODE_ENV
      }
    }
  },
  mounted() {
    this.setupConfig()
    this.addLog('Component mounted, ready to test WebSocket connection')
    this.addLog(`Environment check - VUE_APP_API_URL: ${process.env.VUE_APP_API_URL}`)
    this.addLog(`Environment check - VUE_APP_WEBSOCKET_HOST: ${process.env.VUE_APP_WEBSOCKET_HOST}`)
    this.addLog(`Environment check - VUE_APP_WEBSOCKET_PORT: ${process.env.VUE_APP_WEBSOCKET_PORT}`)
    this.addLog(`Environment check - VUE_APP_WEBSOCKET_KEY: ${process.env.VUE_APP_WEBSOCKET_KEY}`)
  },
  methods: {
    setupConfig() {
      const token = this.$store.getters['auth/token'] || localStorage.getItem('token')
      this.config = {
        broadcaster: 'pusher',
        key: process.env.VUE_APP_WEBSOCKET_KEY || 'healthy-tantra-key',
        wsHost: process.env.VUE_APP_WEBSOCKET_HOST || '127.0.0.1',
        wsPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        wssPort: parseInt(process.env.VUE_APP_WEBSOCKET_PORT) || 6001,
        forceTLS: process.env.VUE_APP_WEBSOCKET_SCHEME === 'wss',
        encrypted: process.env.VUE_APP_WEBSOCKET_SCHEME === 'wss',
        cluster: 'eu', // Required for Pusher, but ignored by Reverb
        authEndpoint: `${process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'}/api/broadcasting/auth`,
        tokenFromStore: this.$store.getters['auth/token'],
        tokenFromLocalStorage: localStorage.getItem('token'),
        finalToken: token,
        userFromStore: this.user
      }
    },

    async testConnection() {
      this.isConnecting = true
      this.connectionStatus = 'Connecting'
      this.addLog('Starting connection test...', 'info')

      try {
        // First test if we have auth token
        if (!this.hasToken) {
          throw new Error('No authentication token available')
        }

        this.addLog('Token available, attempting WebSocket connection...', 'info')

        // Test connection using our WebSocket service
        const echo = WebSocketService.connect()
        
        if (echo) {
          this.addLog('WebSocket service connect() returned echo instance', 'success')
          
          // Check if connected
          setTimeout(() => {
            const isConnected = WebSocketService.isConnected()
            this.addLog(`Connection status check: ${isConnected}`, isConnected ? 'success' : 'error')
            
            if (isConnected) {
              this.connectionStatus = 'Connected'
              this.addLog('✅ WebSocket connected successfully!', 'success')
              
              // Try to join user channel if we have a user
              if (this.user && this.user.id) {
                const userChannel = WebSocketService.joinUserChannel(this.user.id)
                if (userChannel) {
                  this.addLog(`✅ Joined user channel: chat.${this.user.id}`, 'success')
                } else {
                  this.addLog(`❌ Failed to join user channel: chat.${this.user.id}`, 'error')
                }
              }
            } else {
              this.connectionStatus = 'Error'
              this.addLog('❌ Connection check failed', 'error')
            }
          }, 2000) // Wait 2 seconds for connection to establish
        } else {
          throw new Error('WebSocket service failed to return echo instance')
        }

      } catch (error) {
        this.connectionStatus = 'Error'
        this.addLog(`❌ Connection error: ${error.message}`, 'error')
        console.error('WebSocket test error:', error)
      }

      this.isConnecting = false
    },

    disconnect() {
      WebSocketService.disconnect()
      this.connectionStatus = 'Disconnected'
      this.addLog('WebSocket disconnected', 'info')
    },

    addLog(message, type = 'info') {
      this.logs.unshift({
        time: new Date().toLocaleTimeString(),
        message: message,
        type: type
      })
      
      // Keep only last 50 logs
      if (this.logs.length > 50) {
        this.logs = this.logs.slice(0, 50)
      }
    },

    clearLogs() {
      this.logs = []
    },

    refreshAuth() {
      this.setupConfig()
      this.addLog('Configuration refreshed', 'info')
      this.addLog(`Token from store: ${this.config.tokenFromStore ? 'Available' : 'Missing'}`, 'info')
      this.addLog(`Token from localStorage: ${this.config.tokenFromLocalStorage ? 'Available' : 'Missing'}`, 'info')
      this.addLog(`Final token: ${this.config.finalToken ? 'Available' : 'Missing'}`, 'info')
      this.addLog(`User: ${this.user ? this.user.user_name || this.user.email : 'Not available'}`, 'info')
    }
  }
}
</script>

<style scoped>
pre {
  font-size: 12px;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  max-height: 200px;
  overflow: auto;
}
</style>